/**
 * The wizard keeps form state under UI-friendly field names
 * (labName, isCureBayLabCenter, dob, ...). The API wants a different,
 * fixed shape per step (see CC_Onboarding_Wizard_postman_collection.json).
 * This is the single place that translation happens, so a field rename
 * on either side only needs a one-line change here.
 */

import { resolveUserRole } from "../services/ccOnboardingApi";

const upper = (v) => (typeof v === "string" && v ? v.toUpperCase() : v);

// Diagnostic Type / Automapping / "Is CureBay CC Center?" are all plain
// numeric codes on the wire now (Pathology=1, Radiology=2, Both=3;
// Yes=1, No=0). 0 is a valid "No" so a truthy check would wrongly drop
// it — only null/undefined/"" count as "not set".
const codeOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/** documents/attachments hold a browser File object locally; the API
 *  wants an already-uploaded file's URL + metadata. Swap this for a
 *  real "upload to storage, get back a URL" call when that endpoint is
 *  available — it is not part of the documented CC Onboarding API. */
export async function describeFile(file) {
  if (!file) return null;
  return {
    fileName: file.name,
    fileUrl: file._uploadedUrl || null, // populate once a real upload exists
    contentType: file.type || "application/octet-stream",
  };
}

export function buildStep1Payload(labInfo) {
  let latStr =
    labInfo.latitude !== undefined && labInfo.latitude !== null && labInfo.latitude !== ""
      ? String(labInfo.latitude).trim()
      : "";
  let lngStr =
    labInfo.longitude !== undefined && labInfo.longitude !== null && labInfo.longitude !== ""
      ? String(labInfo.longitude).trim()
      : "";

  if ((!latStr || !lngStr) && labInfo.location) {
    const m = /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/.exec(labInfo.location);
    if (m) {
      if (!latStr) latStr = m[1];
      if (!lngStr) lngStr = m[2];
    }
  }

  const formattedLocation = latStr && lngStr ? `${latStr}, ${lngStr}` : labInfo.location || "";

  return {
    ccName: labInfo.labName || labInfo.ccName || "",
    labName: labInfo.labName || labInfo.ccName || "",
    mobileNumber: labInfo.mobileNumber || "",
    emailId: labInfo.emailId || labInfo.email || "",
    address: labInfo.address || "",
    location: formattedLocation,
    area: labInfo.area || formattedLocation || "",
    latitude: latStr,
    longitude: lngStr,
    city: labInfo.city || "",
    state: labInfo.stateCode || labInfo.state || "",
    stateCode: labInfo.stateCode || labInfo.state || "",
    pinCode: labInfo.pinCode || "",
    country: labInfo.country || "India",
    zone: labInfo.zone || "",
    zoneCode: labInfo.zoneCode || labInfo.zone || "",
    circle: labInfo.circle || "",
    circleCode: labInfo.circleCode || labInfo.circle || "",
    ccType: "PARTNER",
    receptionNumber: labInfo.receptionNumber || "",
    agreementDate: labInfo.agreementDate || "",
    terminationNoticePeriod: labInfo.terminationNoticePeriod || "",
    userRole: labInfo.userRole || null,
    eclinicCode: labInfo.eclinicCode || null,
    eclinicName: labInfo.eclinicName || null,
    corporateCode: labInfo.corporateCode || labInfo.cooperateCode || null,
    cooperateCode: labInfo.corporateCode || labInfo.cooperateCode || null,
    corporateName: labInfo.corporateName || null,
    diagnosticType: codeOrNull(labInfo.diagnosticType),
    automapping: codeOrNull(labInfo.automapping),
    autoMapping: codeOrNull(labInfo.automapping),
    sendReportToPatient:
      labInfo.sendReportToPatient !== undefined
        ? String(labInfo.sendReportToPatient).toUpperCase() === "YES" || Number(labInfo.sendReportToPatient) === 1
          ? 1
          : 0
        : 0,
    mobilabAvailabity: labInfo.mobilabAvailabity || labInfo.mobilabAvailability || "",
    mobilabAvailability: labInfo.mobilabAvailabity || labInfo.mobilabAvailability || "",
  };
}

export async function buildStep2Payload(contacts) {
  const mapped = await Promise.all(
    contacts.map(async (c) => {
      const smPhoto = await describeFile(c.smPhoto);
      return {
        salutation: c.salutation,
        firstName: c.firstName,
        lastName: c.lastName,
        designation: c.designation,
        mobileNumber: c.mobileNumber,
        emailId: c.email,
        gender: c.gender,
        dateOfBirth: c.dob,
        source: c.source,
        qualification: c.qualification,
        address1: c.address1,
        city: c.city,
        state: c.state,
        pinCode: c.pinCode,
        country: c.country,
        panNumber: c.panNumber,
        // Mandatory Swasthya Mitra photo (req 12). Falls back to the
        // already-uploaded remote URL when the field wasn't re-picked.
        smPhoto: smPhoto?.fileUrl || smPhoto?.fileName || c.remoteSmPhoto || null,
      };
    })
  );
  return { contacts: mapped };
}

export async function buildStep3Payload(testTimings) {
  const attachments = await Promise.all(
    (testTimings.attachments || [])
      .filter((a) => a.file || a.remoteFileUrl)
      .map(async (a) => {
        if (a.file) {
          const desc = await describeFile(a.file);
          return {
            ...desc,
            partnerName: a.partnerName || a.labPartnerName || undefined,
          };
        }
        return {
          fileName: a.remoteFileName || "Attachment",
          fileUrl: a.remoteFileUrl,
          partnerName: a.partnerName || a.labPartnerName || undefined,
        };
      })
  );

  const flatTimings = [];
  (testTimings.timings || []).forEach((t) => {
    const days = Array.isArray(t.days) && t.days.length ? t.days : t.day ? [t.day] : ["Monday"];
    days.forEach((d) => {
      flatTimings.push({
        day: upper(d),
        opensAt: t.isClosed ? null : t.opensAt,
        closesAt: t.isClosed ? null : t.closesAt,
        isClosed: !!t.isClosed,
      });
    });
  });

  return {
    testDetailsCity: testTimings.city,
    description: testTimings.description,
    timings: flatTimings,
    attachments: attachments.filter(Boolean),
  };
}

function labRowsToMapping(rows, diagnosticType, isPreferred) {
  return (rows || [])
    .filter((r) => r.labCode)
    .map((r) => ({
      diagnosticType: upper(diagnosticType),
      partnerCode: r.labCode,
      partnerName: r.labName || undefined,
      commissionPercentage: r.commission === "" || r.commission === null || r.commission === undefined ? 0 : Number(r.commission),
      isPreferred: isPreferred ? 1 : 0,
      gradeDescription: r.gradeDescription || "",
    }));
}

export function buildStep4Payload(payment) {
  const labMapping = [
    ...labRowsToMapping(payment.pathologyLabs, "PATHOLOGY", false),
    ...labRowsToMapping(payment.preferredPathologyLabs, "PATHOLOGY", true),
    ...labRowsToMapping(payment.radiologyLabs, "RADIOLOGY", false),
    ...labRowsToMapping(payment.preferredRadiologyLabs, "RADIOLOGY", true),
  ];
  return {
    paymentPeriod: upper(payment.paymentPeriod),
    paymentReconciliation: upper(payment.paymentReconciliation),
    commissionPercentage: payment.commissionPercentage,
    priority: upper(payment.priority),
    doctorPanType: upper(payment.doctorPanType),
    isCurebayCc: codeOrNull(payment.isCureBayLabCenter),
    labMapping,
  };
}

// Agreement Date / Termination Notice Period live under the Compliance &
// Registration step on the backend (GET /detail's Tab 5 returns them
// alongside gstNumber/nablNumber/panNumber) — but the wizard shows and
// lets the user edit both fields on Step 1 (CC Information) for
// convenience. `labInfo` is passed in here so Step 5's save keeps them
// where the backend expects them, without moving the input fields off
// Step 1.
export function buildStep5Payload(compliance, labInfo = {}) {
  return {
    gstRegistrationNumber: compliance.gstNumber,
    nablNumber: compliance.nablNumber,
    panNumber: compliance.panNumber,
    agreementDate: labInfo.agreementDate,
    terminationNoticePeriod: labInfo.terminationNoticePeriod,
  };
}

export function buildStep6Payload(bank) {
  return {
    bankName: bank.bankName,
    accountHolderName: bank.accountHolderName,
    accountNumber: bank.accountNumber,
    ifscCode: bank.ifscCode,
    branchLocation: bank.branchLocation,
  };
}

export async function buildStep7Payload(documents) {
  const [contractCopy, cancelledCheque, ccLogo, kycDocument, panCard, gstCertificate] = await Promise.all([
    describeFile(documents.contractCopy),
    describeFile(documents.bankProof),
    describeFile(documents.labLogo),
    describeFile(documents.kycDocument),
    describeFile(documents.panCard),
    describeFile(documents.gstCertificate),
  ]);
  const panVal =
    panCard?.fileUrl ||
    panCard?.fileName ||
    documents.panCardPhoto ||
    documents.panProof ||
    documents.remote?.panCard ||
    null;
  const agreementVal =
    contractCopy?.fileUrl ||
    contractCopy?.fileName ||
    documents.agreementCopy ||
    documents.contractCopy ||
    documents.remote?.contractCopy ||
    null;
  const chequeVal =
    cancelledCheque?.fileUrl ||
    cancelledCheque?.fileName ||
    documents.cancelledCheque ||
    documents.bankProof ||
    documents.remote?.bankProof ||
    null;
  const logoVal =
    ccLogo?.fileUrl ||
    ccLogo?.fileName ||
    documents.ccLogo ||
    documents.labLogo ||
    documents.remote?.labLogo ||
    null;
  const aadharVal =
    kycDocument?.fileUrl ||
    kycDocument?.fileName ||
    documents.aadharKyc ||
    documents.kycDocument ||
    documents.remote?.kycDocument ||
    null;
  const gstVal =
    gstCertificate?.fileUrl ||
    gstCertificate?.fileName ||
    documents.gstCertificate ||
    documents.remote?.gstCertificate ||
    null;

  return {
    attachmentIds: documents.attachmentIds || [],
    agreementCopy: agreementVal,
    contractCopy: agreementVal,
    cancelledCheque: chequeVal,
    bankProof: chequeVal,
    ccLogo: logoVal,
    labLogo: logoVal,
    aadharKyc: aadharVal,
    kycDocument: aadharVal,
    panCardPhoto: panVal,
    panCard: panVal,
    panProof: panVal,
    gstCertificate: gstVal,
  };
}

/** stepNo is 1-based, matching /draft/step/{stepNo} */
export async function buildStepPayload(stepNo, form) {
  switch (stepNo) {
    case 1:
      return buildStep1Payload(form.labInfo);
    case 2:
      return buildStep2Payload(form.contacts);
    case 3:
      return buildStep3Payload(form.testTimings);
    case 4:
      return buildStep4Payload(form.payment);
    case 5:
      return buildStep5Payload(form.compliance, form.labInfo);
    case 6:
      return buildStep6Payload(form.bank);
    case 7:
      return buildStep7Payload(form.documents);
    default:
      throw new Error(`No payload mapper for step ${stepNo}`);
  }
}

export const STEP_ID_TO_NO = {
  labInfo: 1,
  contacts: 2,
  testTimings: 3,
  payment: 4,
  compliance: 5,
  bank: 6,
  documents: 7,
};

// ---------------------------------------------------------------------
// Reverse mappers — API step payload -> local wizard form shape.
// Used to rehydrate the wizard when resuming a saved draft
// (GET /draft/{draftKey}). Each mirrors its buildStepNPayload sibling
// above field-for-field, so a rename on either side only ever needs
// updating in one matching pair.
//
// ASSUMPTION: GET /draft/{draftKey} returns the steps' saved bodies
// verbatim (CC_ONBOARDING_DRAFT stores "payload as JSON" per the
// schema doc), as either an array of `{ stepNo, payload }` records or
// an object keyed by step number. normalizeDraftResponse() below
// accepts either shape — adjust it if the live response differs.
// ---------------------------------------------------------------------

const lower = (v) => (typeof v === "string" && v ? v.toLowerCase() : v);
const titleCase = (v) => (typeof v === "string" && v ? v[0] + v.slice(1).toLowerCase() : v);

const PAYMENT_PERIOD_REVERSE = { WEEKLY: "weekly", BIWEEKLY: "biweekly", MONTHLY: "monthly" };
const RECONCILIATION_REVERSE = { AUTO: "auto", MANUAL: "manual" };
const PRIORITY_REVERSE = { HIGH: "high", MEDIUM: "medium", LOW: "low" };
const PAN_TYPE_REVERSE = { INDIVIDUAL: "individual", FIRM: "firm" };
const DIAGNOSTIC_TYPE_NAME_TO_CODE = { PATHOLOGY: 1, RADIOLOGY: 2, BOTH: 3 };

// The API sends diagnosticType either as the numeric code (1/2/3) or as the
// name ("PATHOLOGY"/"RADIOLOGY"/"BOTH") — accept either and normalise to the
// code the UI state uses.
//
// It no longer defaults to Pathology. That default was hiding a bug rather than
// solving one: /detail returns the facility KIND under `diagnosticType`
// ("Collection Center"), which parses as neither a code nor a name, so every
// facility resolved to Pathology and saving wrote Pathology back over the real
// category. A blank radio on a required field is visible and recoverable; a
// wrong value pre-selected for the user is neither.
function normalizeDiagnosticType(v) {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") return v;
  const asNum = Number(v);
  if (!Number.isNaN(asNum)) return asNum;

  // A word we recognise (PATHOLOGY / RADIOLOGY / BOTH), or nothing.
  //
  // This used to default to 1 for anything unparseable, which silently turned
  // "Collection Center" — the facility KIND that /detail returns under
  // `diagnosticType` — into Pathology. Every facility then displayed as
  // Pathology whatever it really was, and saving from that screen wrote
  // Pathology back over the real category.
  //
  // "" leaves the radio genuinely unset, which is visible and safe: a required
  // field the user must answer beats a wrong answer pre-selected for them.
  return DIAGNOSTIC_TYPE_NAME_TO_CODE[upper(v)] ?? "";
}

// Same idea for the Yes/No radios (automapping, "Is CureBay CC Center?"):
// accept boolean, 1/0, "1"/"0", or "Y"/"N"/"Yes"/"No" from the API and
// always normalize to 1/0. `defaultVal` lets a specific field opt into a
// concrete fallback (e.g. automapping defaults to 0/No) instead of "".
function normalizeYesNo(v, defaultVal = "") {
  if (v === null || v === undefined || v === "") return defaultVal;
  if (v === true || v === 1 || v === "1") return 1;
  if (v === false || v === 0 || v === "0") return 0;
  if (typeof v === "string") {
    const s = v.trim().toUpperCase();
    if (s === "Y" || s === "YES") return 1;
    if (s === "N" || s === "NO") return 0;
  }
  return defaultVal;
}

// The first of several candidate keys that reads as a diagnostic category.
//
// Each is normalized on its own rather than picked with `??` and normalized once,
// because a present-but-useless value (0, "", "Collection Center") has to fall
// through to the next candidate instead of ending the search.
function firstDiagnosticType(...candidates) {
  for (const candidate of candidates) {
    const code = normalizeDiagnosticType(candidate);
    if (code === 1 || code === 2 || code === 3) return code;
  }
  return "";
}

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function mapStep1FromApi(api = {}) {
  const hasEclinic = !!(api.eclinicCode);
  const hasCorporate = !!(api.corporateCode ?? api.cooperateCode);
  const lat = api.latitude ?? api.lat ?? "";
  const lng = api.longitude ?? api.lng ?? api.lon ?? "";
  let locStr = "";
  if (lat && lng && String(lat).trim() && String(lng).trim()) {
    locStr = `${String(lat).trim()}, ${String(lng).trim()}`;
  } else {
    locStr = api.location ?? api.locationName ?? "";
  }

  return {
    ccName: api.ccName ?? api.labName ?? api.name ?? "",
    labName: api.labName ?? api.ccName ?? api.name ?? "",
    mobileNumber: api.mobileNumber ?? api.contactNumber1 ?? "",
    emailId: api.emailId ?? api.email ?? "",
    location: locStr,
    area: api.area ?? api.location ?? "",
    latitude: lat ? String(lat).trim() : "",
    longitude: lng ? String(lng).trim() : "",
    address: api.address ?? api.address1 ?? "",
    city: api.city ?? "",
    state: api.state ?? "",
    stateCode: api.stateCode ?? (api.state && api.state.length <= 3 ? api.state : ""),
    pinCode: api.pinCode ?? "",
    country: api.country ?? "India",
    zone: api.zone ?? api.zoneCode ?? "",
    zoneCode: api.zoneCode ?? api.zone ?? "",
    circle: api.circle ?? api.circleCode ?? "",
    circleCode: api.circleCode ?? api.circle ?? "",
    receptionNumber: api.receptionNumber ?? "",
    agreementDate: api.agreementDate ?? "",
    terminationNoticePeriod: api.terminationNoticePeriod ?? api.terminationNoticeDays ?? "",
    // Resolved to the dropdown's own option value, whichever spelling arrives.
    userRole:
      resolveUserRole(api.userRole ?? api.userRoleCode) ||
      (hasEclinic ? "Swasthya Mitra Diagnostic" : hasCorporate ? "Cooperate Diagnostics" : ""),
    eclinicCode: api.eclinicCode ?? "",
    eclinicName: api.eclinicName ?? "",
    mobilabId: api.mobilabId ?? "",
    corporateCode: api.corporateCode ?? api.cooperateCode ?? "",
    corporateName: api.corporateName ?? "",
    diagnosticType: firstDiagnosticType(
      api.diagnosticCategoryId,
      api.diagnosticCategory,
      api.diagnosticType,
      api.labType
    ),
    automapping: normalizeYesNo(api.automapping ?? api.autoMapping, 0),
    sendReportToPatient:
      api.sendReportToPatient !== undefined
        ? String(api.sendReportToPatient).toUpperCase() === "YES" || Number(api.sendReportToPatient) === 1
          ? 1
          : 0
        : 0,
    mobilabAvailabity: api.mobilabAvailabity || api.mobilabAvailability || "",
    mobilabAvailability: api.mobilabAvailabity || api.mobilabAvailability || "",
  };
}

export function mapStep2FromApi(api = {}) {
  const list = Array.isArray(api) ? api : api.contacts || [];
  const contacts = list.map((c) => ({
    id: newId(),
    salutation: c.salutation ?? "",
    firstName: c.firstName ?? "",
    lastName: c.lastName ?? "",
    designation: c.designation ?? "",
    mobileNumber: c.mobileNumber ?? c.phoneNumber ?? c.phone ?? "",
    email: c.emailId ?? c.email ?? "",
    gender: c.gender ?? "",
    dob: c.dateOfBirth ?? c.dob ?? "",
    source: c.source ?? "",
    qualification: c.qualification ?? "",
    address1: c.address1 ?? "",
    city: c.city ?? "",
    state: c.state ?? "",
    pinCode: c.pinCode ?? c.pincode ?? "",
    country: c.country ?? "India",
    panNumber: c.panNumber ?? "",
    // Already-uploaded SM photo — shown as a "view/replace" reference
    // rather than forced back through the file picker (req 12).
    smPhoto: null,
    remoteSmPhoto: c.smPhoto ?? c.photo ?? null,
  }));
  return contacts.length ? contacts : undefined; // undefined -> caller keeps the single blank contact
}

/** HH:MM for an `<input type="time">`, from HH:MM or HH:MM:SS. */
function toInputTime(value) {
  if (!value) return "";
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function isAttachmentObject(a) {
  if (!a || typeof a !== "object") return false;
  if (a.dayOfWeek || a.opensAt || a.closesAt || a.firstName || a.mobileNumber || a.partnerCode || a.commissionPercentage) {
    return false;
  }
  return Boolean(
    a.fileUrl ||
      a.viewUrl ||
      a.publicUrl ||
      a.fileName ||
      a.storedFileName ||
      a.contentType ||
      a.targetAttachmentId ||
      a.attachmentId ||
      (a.description && (a.description.includes("Photo") || a.description.includes("Copy") || a.description.includes("Attachment") || a.description.includes("Card") || a.description.includes("Cheque") || a.description.includes("Logo") || a.description.includes("KYC") || a.description.includes("PAN") || a.description.includes("GST") || a.description.includes("Document")))
  );
}

export function mapStep3FromApi(api = {}) {
  const rawTimings = api.timings || [];
  const groupedSlots = [];
  const slotMap = new Map();

  rawTimings.forEach((t) => {
    const rawDay = titleCase(t.day ?? t.dayOfWeek) ?? "Monday";
    const opensAt = toInputTime(t.opensAt);
    const closesAt = toInputTime(t.closesAt);
    const isClosed = !!t.isClosed;
    const key = `${opensAt}__${closesAt}__${isClosed}`;

    if (slotMap.has(key)) {
      const existingSlot = slotMap.get(key);
      if (!existingSlot.days.includes(rawDay)) {
        existingSlot.days.push(rawDay);
      }
    } else {
      const newSlot = {
        id: newId(),
        days: [rawDay],
        opensAt,
        closesAt,
        isClosed,
      };
      slotMap.set(key, newSlot);
      groupedSlots.push(newSlot);
    }
  });

  const rawAttachments = api.testAttachments || api.attachments || [];
  const seen = new Set();
  const attachments = [];
  rawAttachments.forEach((a) => {
    if (!isAttachmentObject(a)) return;
    const url = a.viewUrl || a.publicUrl || a.fileUrl || a.url || "";
    const name = a.fileName || a.storedFileName || "";
    const partner = a.partnerName || a.labPartnerName || "";
    const idKey = a.id ? `id_${a.id}` : `${url}_${name}_${partner}`;
    if (!seen.has(idKey)) {
      seen.add(idKey);
      attachments.push({
        id: a.id ? String(a.id) : newId(),
        attachmentId: a.id,
        partnerName: partner,
        description: a.description || (partner ? `Test Attachment - ${partner}` : "Test Rate Card"),
        file: null,
        remoteFileName: name,
        remoteFileUrl: url,
      });
    }
  });

  return {
    city: api.testDetailsCity ?? "",
    description: api.description ?? "",
    timings: groupedSlots.length
      ? groupedSlots
      : [
          {
            id: newId(),
            days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            opensAt: "08:00",
            closesAt: "17:00",
            isClosed: false,
          },
        ],
    attachments,
  };
}

function mappingToRow(m) {
  return {
    id: newId(),
    labCode: m.partnerCode ?? "",
    labName: m.partnerName ?? m.partnerCode ?? "",
    commission: m.commissionPercentage ?? "",
    gradeDescription: m.gradeDescription ?? "",
  };
}

export function mapStep4FromApi(api = {}) {
  const rows = api.labMapping || [];

  // Discipline as a word, whatever shape it arrives in.
  //
  // `(m.diagnosticType || "").toUpperCase()` threw outright on a numeric 1 or 2 —
  // `1 || ""` is the number 1, and numbers have no toUpperCase — taking the whole
  // step-4 hydration down with it. String() first, and the 1/2 codes are accepted
  // as well as the names.
  // "Both"/3 counts as both, rather than as neither. Rows are normally written
  // per-discipline, but "Both" is a legal value of this field elsewhere in the
  // flow, and a row that matches no group disappears from the screen silently —
  // the worst of the three possible outcomes.
  const disciplineOf = (m) => {
    const raw = String(m.diagnosticType ?? "").trim().toUpperCase();
    if (raw === "1") return "PATHOLOGY";
    if (raw === "2") return "RADIOLOGY";
    if (raw === "3") return "BOTH";
    return raw;
  };
  const inDiscipline = (m, type) => {
    const d = disciplineOf(m);
    return d === type || d === "BOTH";
  };

  const byGroup = (type, preferred) => {
    // One row per lab. The API can return the same lab twice — the insert-only
    // materialise left an old row and a new row both active, at two different
    // commissions — and this used to render both, which is how a commission edited
    // from 18% to 25% showed up as two identical labs charging different rates.
    // Showing one of them and silently dropping the other would be worse: the
    // duplicate is real and needs fixing in the data, so the screen names it.
    //
    // The FIRST occurrence wins, because /detail returns these newest-id-first and
    // the newest is the row the last approval wrote. This has to agree with
    // MdmPubSubServiceImpl, which sorts by id descending and publishes the first
    // it sees: if the screen kept the last row and MDM kept the first, the two
    // would show different commissions for the same lab from the same data.
    const seen = new Map();
    let duplicates = 0;

    rows
      .filter((m) => inDiscipline(m, type) && normalizeYesNo(m.isPreferred, 0) === (preferred ? 1 : 0))
      .forEach((m, index) => {
        // A row with no lab code cannot be compared to another row with no lab
        // code, so it is keyed uniquely rather than merged. Collapsing them would
        // hide one mapping and report a duplicate that is not one.
        const code = m.partnerCode ? String(m.partnerCode) : `__nocode_${index}`;
        if (seen.has(code)) {
          duplicates += 1;
          return;
        }
        seen.set(code, m);
      });

    if (duplicates) {
      // eslint-disable-next-line no-console
      console.warn(
        `[cc-onboarding] ${duplicates} duplicate ${type} lab mapping row(s) collapsed. ` +
          `CC_LAB_MAPPING has more than one active row for the same lab — run sql/onboarding_lab_mapping_dedupe.sql.`
      );
    }
    return Array.from(seen.values()).map(mappingToRow);
  };
  return {
    paymentPeriod: PAYMENT_PERIOD_REVERSE[api.paymentPeriod] ?? lower(api.paymentPeriod) ?? "",
    paymentReconciliation:
      RECONCILIATION_REVERSE[api.paymentReconciliation] ?? lower(api.paymentReconciliation) ?? "",
    commissionPercentage: api.commissionPercentage ?? "",
    priority: PRIORITY_REVERSE[api.priority] ?? lower(api.priority) ?? "",
    doctorPanType: PAN_TYPE_REVERSE[api.doctorPanType] ?? lower(api.doctorPanType) ?? "",
    isCureBayLabCenter: normalizeYesNo(api.isCurebayCc ?? api.isCurebayOwned),
    pathologyLabs: byGroup("PATHOLOGY", false),
    preferredPathologyLabs: byGroup("PATHOLOGY", true),
    radiologyLabs: byGroup("RADIOLOGY", false),
    preferredRadiologyLabs: byGroup("RADIOLOGY", true),
  };
}

export function mapStep5FromApi(api = {}) {
  return {
    gstNumber: api.gstRegistrationNumber ?? api.gstNumber ?? "",
    nablNumber: api.nablNumber ?? "",
    panNumber: api.panNumber ?? "",
    // Not rendered on the Compliance & Registration step itself (it's
    // shown/edited on Step 1 — see mapDraftToForm's Step 1 backfill
    // above) but kept here too so this mapper mirrors what the API
    // actually returns for this tab.
    agreementDate: api.agreementDate ?? "",
    terminationNoticePeriod: api.terminationNoticePeriod ?? api.terminationNoticeDays ?? "",
  };
}

export function mapStep6FromApi(api = {}) {
  return {
    bankName: api.bankName ?? "",
    accountHolderName: api.accountHolderName ?? "",
    accountNumber: api.accountNumber ?? "",
    ifscCode: api.ifscCode ?? "",
    branchLocation: api.branchLocation ?? "",
  };
}

export function mapStep7FromApi(api = {}) {
  if (Array.isArray(api)) {
    const byDesc = {};
    const byDescId = {};
    api.forEach((a) => {
      if (!a) return;
      const desc = a.description || a.slot || a.fileName || "";
      const url = a.viewUrl || a.publicUrl || a.fileUrl || a.url || (typeof a === "string" ? a : "");
      if (desc && url) {
        byDesc[desc] = url;
        if (a.id) byDescId[desc] = a.id;
      }
    });

    const attachmentIdsMap = {
      contractCopy: byDescId["Agreement Copy"] || byDescId["Contract Copy"] || byDescId["agreementCopy"] || null,
      bankProof: byDescId["Cancelled Cheque"] || byDescId["Bank Proof"] || byDescId["cancelledCheque"] || null,
      labLogo: byDescId["CC Logo"] || byDescId["Lab Logo"] || byDescId["ccLogo"] || null,
      kycDocument: byDescId["Aadhaar KYC"] || byDescId["KYC Document"] || byDescId["aadharKyc"] || null,
      panCard: byDescId["PAN Card"] || byDescId["PAN Proof"] || byDescId["panCardPhoto"] || byDescId["panCard"] || null,
      gstCertificate: byDescId["GST Certificate"] || byDescId["gstCertificate"] || null,
    };

    const remote = {
      contractCopy: byDesc["Agreement Copy"] || byDesc["Contract Copy"] || byDesc["agreementCopy"] || null,
      bankProof: byDesc["Cancelled Cheque"] || byDesc["Bank Proof"] || byDesc["cancelledCheque"] || null,
      labLogo: byDesc["CC Logo"] || byDesc["Lab Logo"] || byDesc["ccLogo"] || null,
      kycDocument: byDesc["Aadhaar KYC"] || byDesc["KYC Document"] || byDesc["aadharKyc"] || null,
      panCard: byDesc["PAN Card"] || byDesc["PAN Proof"] || byDesc["panCardPhoto"] || byDesc["panCard"] || null,
      gstCertificate: byDesc["GST Certificate"] || byDesc["gstCertificate"] || null,
    };

    return {
      contractCopy: null,
      bankProof: null,
      labLogo: null,
      kycDocument: null,
      panCard: null,
      gstCertificate: null,
      agreementCopy: remote.contractCopy,
      cancelledCheque: remote.bankProof,
      ccLogo: remote.labLogo,
      aadharKyc: remote.kycDocument,
      panCardPhoto: remote.panCard,
      remote,
      attachmentIdsMap,
      attachmentIds: Object.values(attachmentIdsMap).filter(Boolean),
    };
  }

  const contractCopyUrl = api.agreementCopy ?? api.contractCopy ?? api.agreement ?? null;
  const bankProofUrl = api.cancelledCheque ?? api.bankProof ?? api.cheque ?? null;
  const labLogoUrl = api.ccLogo ?? api.labLogo ?? api.logo ?? null;
  const kycDocumentUrl = api.aadharKyc ?? api.kycDocument ?? api.aadhar ?? null;
  const panCardUrl = api.panCardPhoto ?? api.panCard ?? api.panProof ?? api.panPhoto ?? null;
  const gstCertificateUrl = api.gstCertificate ?? api.gst ?? null;

  return {
    contractCopy: null,
    bankProof: null,
    labLogo: null,
    kycDocument: null,
    panCard: null,
    gstCertificate: null,
    agreementCopy: contractCopyUrl,
    cancelledCheque: bankProofUrl,
    ccLogo: labLogoUrl,
    aadharKyc: kycDocumentUrl,
    panCardPhoto: panCardUrl,
    remote: {
      contractCopy: contractCopyUrl,
      bankProof: bankProofUrl,
      labLogo: labLogoUrl,
      kycDocument: kycDocumentUrl,
      panCard: panCardUrl,
      gstCertificate: gstCertificateUrl,
    },
    attachmentIdsMap: api.attachmentIdsMap || {},
    attachmentIds: Array.isArray(api.attachmentIds) ? api.attachmentIds : [],
  };
}

const REVERSE_MAPPERS = {
  1: { section: "labInfo", map: mapStep1FromApi },
  2: { section: "contacts", map: mapStep2FromApi },
  3: { section: "testTimings", map: mapStep3FromApi },
  4: { section: "payment", map: mapStep4FromApi },
  5: { section: "compliance", map: mapStep5FromApi },
  6: { section: "bank", map: mapStep6FromApi },
  7: { section: "documents", map: mapStep7FromApi },
};

/**
 * Accepts whatever GET /draft/{draftKey} returns and normalizes it to
 * `{ [stepNo]: payloadObject }`, regardless of whether the API sends
 * an array of `{stepNo, payload}` records or an object keyed by step
 * number. Adjust here first if the live shape differs from the
 * assumption above — every caller goes through this one function.
 */
/**
 * One step's payload, whichever endpoint produced the row.
 *
 * GET /draft/{draftKey} rows carry the payload in `stepPayload` as a JSON
 * STRING — that is stored verbatim on the server, exactly as the wizard sent
 * it. GET /{centerCode}/detail tabs carry it as an already-parsed object in
 * `data`.
 *
 * This used to look for `payload ?? body ?? data ?? s` and never for
 * `stepPayload`, so a draft row fell through to `s` — the row itself. The
 * mappers then read `.ccName`, `.city`, `.address` off a row that has
 * `stepNo`/`stepPayload`/`status` and nothing else, so every field resolved to
 * undefined and the wizard rendered an empty form. Saving from there wrote the
 * blanks back, which is how a parked edit ends up reading as "everything
 * cleared".
 */
function readStepPayload(row) {
  if (!row || typeof row !== "object") return null;

  const raw = row.stepPayload ?? row.payload ?? row.body ?? row.data;
  if (raw && typeof raw === "object") return raw;

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      // A payload that will not parse is not something to guess at — leaving
      // the step unmapped keeps whatever the form already had, which beats
      // wiping the screen because one row is malformed.
      return null;
    }
  }

  // No recognised payload key. Only treat the row itself as the payload when it
  // actually looks like one, so a draft row can never masquerade as content.
  const looksLikeRow = "stepNo" in row || "stepPayload" in row || "isComplete" in row;
  return looksLikeRow ? null : row;
}

export function normalizeDraftResponse(raw) {
  if (!raw) return {};
  // GET .../detail (view/edit an already-submitted center) returns
  // { tabs: [{ step, name, data }, ...] } — same { key: stepNo, key: payload }
  // shape as a draft's steps once step/data are picked up below, so it
  // flows through the same normalization as GET /draft/{draftKey}.
  const steps = Array.isArray(raw) ? raw : raw.steps || raw.draftSteps || raw.tabs || [];
  if (Array.isArray(steps) && steps.length) {
    return steps.reduce((acc, s) => {
      const stepNo = s.stepNo ?? s.step_no ?? s.step;
      const payload = readStepPayload(s);
      if (stepNo && payload) acc[stepNo] = payload;
      return acc;
    }, {});
  }
  // Fallback: object already keyed by step number, e.g. {1: {...}, 2: {...}}
  if (typeof raw === "object") {
    return Object.fromEntries(
      Object.entries(raw).filter(([k]) => REVERSE_MAPPERS[k] || REVERSE_MAPPERS[Number(k)])
    );
  }
  return {};
}

/**
 * Turns a normalized draft response into a partial form object keyed
 * by section (labInfo, contacts, testTimings, ...), ready to merge
 * into the wizard's local reducer state via HYDRATE_FORM.
 */
export function mapDraftToForm(rawDraftResponse) {
  const byStep = normalizeDraftResponse(rawDraftResponse);

  // Agreement Date / Termination Notice Period are returned by the API
  // under the Compliance & Registration step's own data (Step 5 / Tab 5)
  // — that stays the source of truth — but the wizard also shows and
  // lets the user edit them on Step 1 at view/edit time. Backfill Step
  // 1's payload from Step 5 whenever Step 1 didn't already carry them
  // (e.g. a freshly-saved draft, where Step 1 sends them itself), so the
  // field is populated either way.
  const step5 = byStep[5] ?? byStep["5"];
  if (step5) {
    const step1 = byStep[1] ?? byStep["1"] ?? {};
    byStep[1] = {
      ...step1,
      agreementDate: step1.agreementDate ?? step5.agreementDate ?? "",
      terminationNoticePeriod:
        step1.terminationNoticePeriod ??
        step1.terminationNoticeDays ??
        step5.terminationNoticePeriod ??
        step5.terminationNoticeDays ??
        "",
    };
  }

  // Diagnostic Type comes off the Location tab (tab 10), so backfill step 1 the
  // same way step 5 backfills the agreement dates above.
  //
  // The category is stored on LOCATION.DIAGNOSTICCATEGORYID and /detail publishes
  // it on tab 10. REVERSE_MAPPERS only has entries for steps 1-7, so tabs 8-11 are
  // dropped by the `if (!entry) return` below — mapStep1FromApi was reading
  // `diagnosticCategoryId` off an object that never contains it, and tab 1's own
  // `diagnosticType` is the facility KIND ("Collection Center"), which resolves to
  // nothing. Hence a required radio group with no option selected on a record
  // whose category is perfectly well known.
  //
  // Step 1 still wins where it has a value, so an in-progress edit is not
  // overwritten by what is currently live.
  const locationTab = byStep[10] ?? byStep["10"];
  if (locationTab) {
    const step1 = byStep[1] ?? byStep["1"] ?? {};
    byStep[1] = {
      ...step1,
      diagnosticCategoryId:
        step1.diagnosticCategoryId ?? locationTab.diagnosticCategoryId ?? locationTab.diagnosticCategory ?? "",
    };
  }

  const form = {};
  Object.entries(byStep).forEach(([stepNo, payload]) => {
    const entry = REVERSE_MAPPERS[stepNo] || REVERSE_MAPPERS[Number(stepNo)];
    if (!entry) return;
    const mapped = entry.map(payload);
    if (mapped !== undefined) form[entry.section] = mapped;
  });

  // Collect and group all attachments across detail tabs/root
  const allAttachments = [];
  if (Array.isArray(rawDraftResponse?.attachments)) {
    allAttachments.push(...rawDraftResponse.attachments.filter(isAttachmentObject));
  }
  const tabs = Array.isArray(rawDraftResponse)
    ? rawDraftResponse
    : rawDraftResponse?.tabs || rawDraftResponse?.steps || [];
  tabs.forEach((t) => {
    const data = t.data || t.stepPayload || t;
    if (Array.isArray(data)) {
      allAttachments.push(...data.filter(isAttachmentObject));
    } else if (data && typeof data === "object") {
      if (Array.isArray(data.attachments)) allAttachments.push(...data.attachments.filter(isAttachmentObject));
      if (Array.isArray(data.testAttachments)) allAttachments.push(...data.testAttachments.filter(isAttachmentObject));
    }
  });

  // Deduplicate attachments by id or url/name
  const seenAtt = new Set();
  const dedupedAttachments = [];
  allAttachments.forEach((a) => {
    if (!isAttachmentObject(a)) return;
    const key = a.id ? `id_${a.id}` : `${a.viewUrl || a.fileUrl || ""}_${a.fileName || ""}_${a.description || ""}`;
    if (!seenAtt.has(key)) {
      seenAtt.add(key);
      dedupedAttachments.push(a);
    }
  });

  const bySlot = {};
  const bySlotId = {};
  dedupedAttachments
    .filter((a) => a && (a.status === undefined || a.status === 1 || a.status === true))
    .forEach((a) => {
      const slot = a.description || a.slot || a.fileName;
      if (slot) {
        bySlot[slot] = a.viewUrl || a.publicUrl || a.fileUrl || a;
        if (a.id) bySlotId[slot] = a.id;
      }
    });

  // 1. SM Photo
  if (bySlot["SM Photo"] && form.contacts && form.contacts.length) {
    form.contacts[0].remoteSmPhoto = bySlot["SM Photo"];
    if (bySlotId["SM Photo"]) {
      form.contacts[0].attachmentId = bySlotId["SM Photo"];
    }
  }

  // 2. Test Timings attachments
  const testDraftAtts = dedupedAttachments.filter(
    (a) => (Number(a.stepNo) === 3 || a.labPartnerName || a.description?.includes("Test") || a.description?.includes("Rate")) && isAttachmentObject(a)
  );
  if (testDraftAtts.length) {
    form.testTimings = form.testTimings || {};
    form.testTimings.attachments = testDraftAtts.map((a) => ({
      id: a.id ? String(a.id) : newId(),
      attachmentId: a.id,
      partnerName: a.labPartnerName || a.partnerName || "",
      description: a.description || "Test Rate Card",
      file: null,
      remoteFileUrl: a.viewUrl || a.publicUrl || a.fileUrl,
      remoteFileName: a.fileName,
    }));
  }

  // 3. Documents tab
  if (!form.documents) {
    form.documents = {
      contractCopy: null,
      bankProof: null,
      labLogo: null,
      kycDocument: null,
      panCard: null,
      gstCertificate: null,
      remote: {},
      attachmentIdsMap: {},
      attachmentIds: [],
    };
  }
  form.documents.remote = {
    ...(form.documents.remote || {}),
    contractCopy:
      bySlot["Agreement Copy"] ||
      bySlot["agreementCopy"] ||
      bySlot["Contract Copy"] ||
      form.documents.remote?.contractCopy ||
      null,
    bankProof:
      bySlot["Cancelled Cheque"] ||
      bySlot["cancelledCheque"] ||
      bySlot["Bank Proof"] ||
      bySlot["Bank Passbook"] ||
      form.documents.remote?.bankProof ||
      null,
    labLogo:
      bySlot["CC Logo"] ||
      bySlot["ccLogo"] ||
      bySlot["Lab Logo"] ||
      form.documents.remote?.labLogo ||
      null,
    kycDocument:
      bySlot["Aadhaar KYC"] ||
      bySlot["aadharKyc"] ||
      bySlot["KYC Document"] ||
      form.documents.remote?.kycDocument ||
      null,
    panCard:
      bySlot["PAN Card"] ||
      bySlot["panCardPhoto"] ||
      bySlot["panCard"] ||
      bySlot["PAN"] ||
      bySlot["PAN Proof"] ||
      form.documents.remote?.panCard ||
      null,
    gstCertificate:
      bySlot["GST Certificate"] ||
      bySlot["gstCertificate"] ||
      form.documents.remote?.gstCertificate ||
      null,
  };

  form.documents.attachmentIdsMap = {
    ...(form.documents.attachmentIdsMap || {}),
    contractCopy: bySlotId["Agreement Copy"] || bySlotId["Contract Copy"] || bySlotId["agreementCopy"] || null,
    bankProof: bySlotId["Cancelled Cheque"] || bySlotId["cancelledCheque"] || bySlotId["Bank Proof"] || null,
    labLogo: bySlotId["CC Logo"] || bySlotId["ccLogo"] || bySlotId["Lab Logo"] || null,
    kycDocument: bySlotId["Aadhaar KYC"] || bySlotId["aadharKyc"] || bySlotId["KYC Document"] || null,
    panCard: bySlotId["PAN Card"] || bySlotId["panCardPhoto"] || bySlotId["panCard"] || null,
    gstCertificate: bySlotId["GST Certificate"] || bySlotId["gstCertificate"] || null,
  };
  form.documents.attachmentIds = Object.values(form.documents.attachmentIdsMap).filter(Boolean);

  return form;
}
